import React, {useMemo, useState} from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import {IconButton, styled, Typography} from '@mui/material';
import {
  AddMoreButton,
  DropZone,
  FileActionsOverlay,
  FilePreviewGrid,
  Gallery,
  RootContainer,
} from '@securspace/securspace-ui-kit';
import type {StagedFile, ValidationError} from '@securspace/securspace-ui-kit';
import {useSnackbar} from '../../../../context/snackbar-context';
import {
  CHECK_IN_IMAGE_ACCEPTED_TYPES,
  CHECK_IN_IMAGE_MAX_FILES,
  CheckInImageValidationError,
} from '../../../../hooks/checkin/useCheckInImageUpload';
import EditIcon from "@mui/icons-material/Edit";
import CropIcon from "@mui/icons-material/Crop";
import ImagePreviewModal, {type ImagePreviewAction} from "../../../imageUpload/ImagePreviewModal";
import CropPanel from "../../../imageUpload/CropPanel";

interface ImageUploadSectionProps {
  files: StagedFile[];
  onFilesAdded: (files: File[]) => CheckInImageValidationError[];
  onFileRemoved: (file: StagedFile) => void;
  /** Replaces a staged file's preview (e.g. after a crop). */
  onFileReplaced: (file: StagedFile, updated: StagedFile) => void;
}

const ActionIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.common.white,
  border: '2px solid #fff',
  borderRadius: '50%',
}));

/**
 * UIKit-backed image selector for the redesigned check-in flow.
 * The parent owns file state and the associated preview URL lifecycle.
 */
const ImageUploadSection = ({files, onFilesAdded, onFileRemoved, onFileReplaced}: ImageUploadSectionProps) => {
  const {snackbarShowMessage} = useSnackbar();

  // Caller owns the preview modal's open/close state (per the UIKit contract):
  // the pencil action stages a file here and the modal renders while it is
  // set. Newly uploaded images are *not* auto-staged here — they land
  // directly in the grid, and previewing/cropping is always an explicit,
  // opt-in action. This avoids surfacing the Crop panel's "Cancel" button as
  // the first thing a user sees right after uploading, which read as
  // "cancel my upload" rather than "skip cropping" (see ImageUploadSection
  // docs / component-map for the underlying UX rationale).
  const [previewingFile, setPreviewingFile] = useState<StagedFile | null>(null);

  const previewActions = useMemo<ImagePreviewAction[]>(() => [
    {
      id: 'crop',
      label: 'Crop',
      icon: <CropIcon/>,
      panel: (file, onComplete, onCancel, onError, fullScreen) => (
        <CropPanel
          file={file}
          onComplete={onComplete}
          onCancel={onCancel}
          onError={onError}
          fullScreen={fullScreen}
        />
      ),
    },
  ], []);

  const handleFilesAdded = (addedFiles: File[]) => {
    const errors = onFilesAdded(addedFiles);
    const uniqueMessages = Array.from(new Set(errors.map(({message}) => message)));
    uniqueMessages.forEach((message) => snackbarShowMessage(message, 'warning'));
  };

  const handleValidationFailed = (error: ValidationError) => {
    if (error.code === 'INVALID_FILE_TYPE') {
      snackbarShowMessage('Unsupported file type. Please upload a PNG, JPG or GIF image.', 'warning');
    } else if (error.code === 'MAX_FILES_EXCEEDED') {
      snackbarShowMessage(`You can add up to ${CHECK_IN_IMAGE_MAX_FILES} images total.`, 'warning');
    } else {
      snackbarShowMessage(error.message, 'warning');
    }
  };

  const handlePreview = (file: StagedFile) => {
    setPreviewingFile(file);
  };

  // State-before-revoke ordering: update the modal's own reference first,
  // then delegate the grid update + deferred revocation of the old URL to
  // the hook (which owns the blob URL lifecycle). The modal stays open on
  // the (now-cropped) result so the user can see the outcome before
  // dismissing.
  const handleActionComplete = (updated: StagedFile) => {
    if (!previewingFile || previewingFile.file !== updated.file) return;
    setPreviewingFile(updated);
    onFileReplaced(previewingFile, updated);
  };

  const handleClosePreview = () => {
    setPreviewingFile(null);
  };

  const renderActions = (file: StagedFile) => (
    <FileActionsOverlay>
      <ActionIconButton
        size="large"
        onClick={() => handlePreview(file)}
        aria-label="Preview image"
      >
        <EditIcon />
      </ActionIconButton>
      <ActionIconButton
        size="large"
        onClick={() => onFileRemoved(file)}
        aria-label={`Remove ${file.file.name}`}
      >
        <DeleteIcon/>
      </ActionIconButton>
    </FileActionsOverlay>
  );

  return (
    <>
      <Typography variant="body2" gutterBottom>
        Click below to add images
      </Typography>
      <RootContainer
        files={files}
        onFilesAdded={handleFilesAdded}
        onValidationFailed={handleValidationFailed}
        acceptedFileTypes={CHECK_IN_IMAGE_ACCEPTED_TYPES}
        maxFiles={CHECK_IN_IMAGE_MAX_FILES}
        multiple
      >
        <DropZone>
          <Gallery>
            <AddMoreButton label="Drag and drop images, or click to select images"/>
            <FilePreviewGrid renderActions={renderActions}/>
          </Gallery>
        </DropZone>
        {
          previewingFile && (
            <ImagePreviewModal
              file={previewingFile}
              actions={previewActions}
              fullScreenBreakpoint="md"
              onActionComplete={handleActionComplete}
              onActionError={(message) => snackbarShowMessage(message, 'warning')}
              onClose={handleClosePreview}
            />
          )
        }
      </RootContainer>
    </>
  );
};

export default ImageUploadSection;

